/**
 * AdvertiserApprovalPage — conversion review ("scrub") workflow.
 *
 * Composes: header, advertiser input + status tabs, KPI tiles, queue table
 * with approve / reject actions and an inline reject-reason editor, and an
 * audit trail list. Loading / empty / error states covered.
 *
 * Palette: green-only accent (green-600 / green-700 / green-50), white cards
 * with zinc-200 borders, zinc-900 text, Inter, 6-8px rounded corners. Amber
 * and rose reserved strictly for status chips (at-risk / rejected), never as
 * brand accents. No emerald, sky, blue, orange, purple. No emoji, no dark
 * mode, no competitor branding.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAdvertiserApprovalApprove,
	useAdvertiserApprovalAudit,
	useAdvertiserApprovalQueue,
	useAdvertiserApprovalReject,
} from "./hooks";
import type { ApprovalRow, ApprovalStatus } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-rose-600 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

type StatusTab = "pending" | "approved" | "rejected" | "all";

const STATUS_TABS: { key: StatusTab; label: string }[] = [
	{ key: "pending", label: "Pending" },
	{ key: "approved", label: "Approved" },
	{ key: "rejected", label: "Rejected" },
	{ key: "all", label: "All" },
];

const STATUS_CHIP: Record<ApprovalStatus, string> = {
	pending: "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20",
	approved: "bg-green-50 text-green-700 ring-1 ring-green-600/20",
	rejected: "bg-rose-50 text-rose-700 ring-1 ring-rose-600/20",
};

function fmtUnixSec(s: number | null): string {
	if (!s || !Number.isFinite(s) || s <= 0) return "-";
	const d = new Date(s * 1000);
	if (Number.isNaN(d.getTime())) return "-";
	return d.toISOString().slice(0, 16).replace("T", " ");
}

function IconCheck({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M4 10.5l4 4 8-9" />
		</svg>
	);
}

function IconX({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M5 5l10 10M15 5L5 15" />
		</svg>
	);
}

function KpiTile({
	label,
	value,
	testId,
}: {
	label: string;
	value: string | number;
	testId: string;
}) {
	return (
		<div className={CARD} data-testid={testId}>
			<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
				{label}
			</p>
			<p className="mt-2 text-2xl font-semibold text-zinc-900">{value}</p>
		</div>
	);
}

function StatusChip({ status }: { status: ApprovalStatus }) {
	return (
		<span
			className={`inline-flex rounded-[6px] px-2 py-0.5 text-xs font-semibold ${STATUS_CHIP[status]}`}
		>
			{status}
		</span>
	);
}

export function AdvertiserApprovalPage() {
	const [advertiserId, setAdvertiserId] = useState("");
	const [activeTab, setActiveTab] = useState<StatusTab>("pending");
	const [rejectingId, setRejectingId] = useState<string | null>(null);
	const [rejectReason, setRejectReason] = useState("");

	const queueQuery = useAdvertiserApprovalQueue({
		advertiserId,
		status: activeTab,
	});
	const auditQuery = useAdvertiserApprovalAudit(advertiserId);
	const approveMut = useAdvertiserApprovalApprove();
	const rejectMut = useAdvertiserApprovalReject();

	const items: ApprovalRow[] = queueQuery.data?.items ?? [];
	const auditItems = auditQuery.data?.items ?? [];

	const kpis = useMemo(() => {
		let pending = 0;
		let approved = 0;
		let rejected = 0;
		for (const r of items) {
			if (r.status === "pending") pending++;
			else if (r.status === "approved") approved++;
			else if (r.status === "rejected") rejected++;
		}
		return { pending, approved, rejected, total: items.length };
	}, [items]);

	const hasAdvertiser = advertiserId.trim().length > 0;

	const handleApprove = (id: string) => {
		approveMut.mutate({ id });
	};

	const openReject = (id: string) => {
		setRejectingId(id);
		setRejectReason("");
	};
	const cancelReject = () => {
		setRejectingId(null);
		setRejectReason("");
	};
	const submitReject = (e: FormEvent) => {
		e.preventDefault();
		if (!rejectingId) return;
		const reason = rejectReason.trim();
		if (reason.length < 3) return;
		rejectMut.mutate(
			{ id: rejectingId, reason },
			{
				onSuccess: () => {
					setRejectingId(null);
					setRejectReason("");
				},
			},
		);
	};

	return (
		<main
			data-testid="advertiser-approval-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-10 font-[Inter,system-ui,sans-serif]"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-green-700">
					advertiser-approval
				</p>
				<h1 className="text-3xl font-semibold text-zinc-900">
					Conversion review queue
				</h1>
				<p className="text-sm text-zinc-500">
					Approve or reject pending conversions for a single advertiser.
					Decisions are appended to an immutable audit log.
				</p>
			</header>

			<section
				className={CARD}
				aria-label="Advertiser selection"
				data-testid="advertiser-approval-selector"
			>
				<label
					htmlFor="advertiser-id"
					className="text-xs font-semibold uppercase tracking-wider text-zinc-500"
				>
					Advertiser ID
				</label>
				<input
					id="advertiser-id"
					type="text"
					className={`${INPUT} mt-2`}
					placeholder="e.g. adv_abc123"
					value={advertiserId}
					onChange={(e) => setAdvertiserId(e.currentTarget.value)}
					data-testid="advertiser-approval-advertiser-input"
				/>
				<div
					className="mt-4 flex flex-wrap gap-2"
					role="tablist"
					aria-label="Status filter"
					data-testid="advertiser-approval-tabs"
				>
					{STATUS_TABS.map((t) => {
						const active = t.key === activeTab;
						return (
							<button
								key={t.key}
								type="button"
								role="tab"
								aria-selected={active}
								onClick={() => setActiveTab(t.key)}
								className={`rounded-[6px] px-3 py-1.5 text-xs font-semibold ${
									active
										? "border border-green-700 bg-green-600 text-white"
										: "border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50"
								}`}
								data-testid={`advertiser-approval-tab-${t.key}`}
							>
								{t.label}
							</button>
						);
					})}
				</div>
			</section>

			<section className="grid grid-cols-2 gap-4 md:grid-cols-4">
				<KpiTile
					label="Total in view"
					value={kpis.total}
					testId="advertiser-approval-kpi-total"
				/>
				<KpiTile
					label="Pending"
					value={kpis.pending}
					testId="advertiser-approval-kpi-pending"
				/>
				<KpiTile
					label="Approved"
					value={kpis.approved}
					testId="advertiser-approval-kpi-approved"
				/>
				<KpiTile
					label="Rejected"
					value={kpis.rejected}
					testId="advertiser-approval-kpi-rejected"
				/>
			</section>

			<section className={CARD} aria-label="Approval queue">
				<div className="flex items-baseline justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Queue</h2>
					<p className="text-xs text-zinc-500" data-testid="advertiser-approval-count">
						{hasAdvertiser ? `${items.length} item(s)` : "Enter an advertiser ID"}
					</p>
				</div>

				{!hasAdvertiser ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-approval-need-advertiser"
					>
						Select an advertiser to load the review queue.
					</p>
				) : queueQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-approval-loading"
					>
						Loading queue.
					</p>
				) : queueQuery.error ? (
					<p
						className="mt-3 text-sm text-rose-700"
						role="alert"
						data-testid="advertiser-approval-error"
					>
						{queueQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-approval-empty"
					>
						No conversions in this view.
					</p>
				) : (
					<div className="mt-3 overflow-x-auto">
						<table
							className="w-full text-left text-sm"
							data-testid="advertiser-approval-table"
						>
							<thead>
								<tr className="border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500">
									<th className="py-2 pr-4 font-semibold">Conversion</th>
									<th className="py-2 pr-4 font-semibold">Campaign</th>
									<th className="py-2 pr-4 font-semibold">Status</th>
									<th className="py-2 pr-4 font-semibold">Submitted</th>
									<th className="py-2 pr-4 font-semibold">Reviewed</th>
									<th className="py-2 pr-4 text-right font-semibold">Actions</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{items.map((row) => {
									const isRejectingThis = rejectingId === row.id;
									const busyApprove =
										approveMut.isPending && approveMut.variables?.id === row.id;
									const busyReject =
										rejectMut.isPending && rejectMut.variables?.id === row.id;
									return (
										<tr
											key={row.id}
											className="align-top"
											data-testid={`advertiser-approval-row-${row.id}`}
										>
											<td className="py-2 pr-4 font-mono text-xs text-zinc-700">
												{row.conversion_id}
											</td>
											<td className="py-2 pr-4 text-zinc-700">
												{row.campaign_id ?? "-"}
											</td>
											<td className="py-2 pr-4">
												<StatusChip status={row.status} />
											</td>
											<td className="py-2 pr-4 text-zinc-500">
												{fmtUnixSec(row.created_at)}
											</td>
											<td className="py-2 pr-4 text-zinc-500">
												{fmtUnixSec(row.reviewed_at)}
											</td>
											<td className="py-2 pr-4">
												{row.status === "pending" ? (
													<div className="flex justify-end gap-2">
														<button
															type="button"
															className={BTN_PRIMARY}
															onClick={() => handleApprove(row.id)}
															disabled={busyApprove || busyReject || isRejectingThis}
															data-testid={`advertiser-approval-approve-${row.id}`}
														>
															<span className="inline-flex items-center gap-1">
																<IconCheck className="h-3 w-3" />
																{busyApprove ? "Saving" : "Approve"}
															</span>
														</button>
														<button
															type="button"
															className={BTN_DANGER}
															onClick={() => openReject(row.id)}
															disabled={busyApprove || busyReject}
															data-testid={`advertiser-approval-reject-open-${row.id}`}
														>
															<span className="inline-flex items-center gap-1">
																<IconX className="h-3 w-3" />
																Reject
															</span>
														</button>
													</div>
												) : (
													<span className="text-xs text-zinc-500">
														{row.reason ? row.reason : "-"}
													</span>
												)}
												{isRejectingThis ? (
													<form
														onSubmit={submitReject}
														className="mt-2 flex flex-col gap-2 rounded-[6px] border border-zinc-200 bg-zinc-50 p-2"
														data-testid={`advertiser-approval-reject-form-${row.id}`}
													>
														<label
															htmlFor={`reason-${row.id}`}
															className="text-xs font-semibold text-zinc-700"
														>
															Rejection reason (min 3 chars)
														</label>
														<textarea
															id={`reason-${row.id}`}
															value={rejectReason}
															onChange={(e) =>
																setRejectReason(e.currentTarget.value)
															}
															rows={2}
															className={INPUT}
															maxLength={1000}
															data-testid={`advertiser-approval-reject-input-${row.id}`}
														/>
														{rejectMut.error ? (
															<p
																className="text-xs text-rose-700"
																role="alert"
																data-testid={`advertiser-approval-reject-error-${row.id}`}
															>
																{rejectMut.error.message}
															</p>
														) : null}
														<div className="flex gap-2">
															<button
																type="submit"
																className={BTN_DANGER}
																disabled={
																	busyReject || rejectReason.trim().length < 3
																}
																data-testid={`advertiser-approval-reject-submit-${row.id}`}
															>
																{busyReject ? "Saving" : "Confirm reject"}
															</button>
															<button
																type="button"
																className={BTN_GHOST}
																onClick={cancelReject}
																disabled={busyReject}
																data-testid={`advertiser-approval-reject-cancel-${row.id}`}
															>
																Cancel
															</button>
														</div>
													</form>
												) : null}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<section className={CARD} aria-label="Decision audit trail">
				<h2 className="text-base font-semibold text-zinc-900">Audit trail</h2>
				{!hasAdvertiser ? (
					<p className="mt-3 text-sm text-zinc-500">
						Decisions recorded for the advertiser will appear here.
					</p>
				) : auditQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-approval-audit-loading"
					>
						Loading audit.
					</p>
				) : auditQuery.error ? (
					<p className="mt-3 text-sm text-rose-700" role="alert">
						{auditQuery.error.message}
					</p>
				) : auditItems.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-approval-audit-empty"
					>
						No decisions recorded yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="advertiser-approval-audit-list"
					>
						{auditItems.map((a) => (
							<li
								key={a.id}
								className="flex flex-col gap-1 py-2 text-sm sm:flex-row sm:items-center sm:justify-between"
								data-testid={`advertiser-approval-audit-row-${a.id}`}
							>
								<div className="flex items-center gap-3">
									<span
										className={`inline-flex rounded-[6px] px-2 py-0.5 text-xs font-semibold ${
											a.action === "approve"
												? STATUS_CHIP.approved
												: STATUS_CHIP.rejected
										}`}
									>
										{a.action}
									</span>
									<span className="font-mono text-xs text-zinc-700">
										{a.conversion_id}
									</span>
								</div>
								<div className="flex flex-col items-start gap-0.5 text-xs text-zinc-500 sm:items-end">
									<span>{fmtUnixSec(a.created_at)}</span>
									{a.reason ? (
										<span className="text-zinc-600">{a.reason}</span>
									) : null}
								</div>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AdvertiserApprovalPage;
